import { type ReactElement, useCallback } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { FloatingPopover } from '@/components/common/Floating'
import { type DropdownOptionGroupType, type DropdownOptionType, extractInputProps, type InputProps } from '@/defs/input'
import { cn } from '@/utils/commonUtil'

import { InputDropdown } from '../InputDropdown/InputDropdown'
import { InputSelectCurrent } from '../InputSelectCurrent/InputSelectCurrent'

import styles from './InputSelect.module.scss'

type BaseProps<T extends string = string> = {
  autoFocus?: boolean
  children?: ReactElement
  onSelect?: (value: T) => void
  placeholder?: string
  reset?: DropdownOptionType<T>
  value?: T
} & (
  | {
      optionGroups: DropdownOptionGroupType<T>[]
    }
  | {
      options: DropdownOptionType<T>[]
    }
)

export type Props<T extends string = string> = BaseProps<T> & InputProps

export const InputSelect = <T extends string = string>({
  autoFocus,
  children,
  onSelect,
  placeholder,
  reset,
  size,
  value,
  width,
  ...props
}: Props<T>) => {
  const { inputProps } = extractInputProps(props)

  const optionGroups = 'optionGroups' in props ? props.optionGroups : [{ options: props.options }]
  const hasOptions = optionGroups.some((group) => group.options.length > 0) && onSelect
  const isEmpty = hasOptions && (value === undefined || value === '')
  const optionsFlat = optionGroups.flatMap((o) => o.options)

  const onValueChange = useCallback(
    (value: string) => {
      if (value && onSelect) onSelect(value as T)
    },
    [onSelect],
  )

  const className = children
    ? cn(styles.button, { styles, width })
    : cn(styles.container, { isEmpty, ...inputProps, size, styles, width })

  return (
    <FloatingPopover
      autoOpen={autoFocus}
      content={<InputDropdown onSelect={onValueChange} optionGroups={optionGroups} reset={reset} value={value} />}
      width='full'
    >
      <ButtonRoot autoFocus={autoFocus} className={className}>
        {children ? (
          children
        ) : (
          <InputSelectCurrent
            {...inputProps}
            options={optionsFlat}
            placeholder={placeholder}
            size={size}
            value={value}
          />
        )}
      </ButtonRoot>
    </FloatingPopover>
  )
}
